import { Alert, Button } from 'antd';
import { Link } from 'react-router-dom';
import { MonthStatus } from '../types';
import { daysListText, dataImportLink, formatMonth, formatPeriod } from '../utils';
import styles from '../style.module.scss';

interface Props {
  status?: MonthStatus | null;
  /** Tab đang hiện khối này. */
  view: 'ngay' | 'thang';
  isAdmin?: boolean;
  /** Tab Data tháng, tháng chỉ có số cộng dồn cũ: câu chi tiết (Creator, KC) do tab tự dựng. */
  legacyDetail?: string;
}

const renderAlert = ({ status, view, isAdmin, legacyDetail }: Props) => {
  if (!status) return null;
  const month = formatMonth(status.month);
  const missing = status.missingDays || [];
  const list = daysListText(missing);
  const goTo = (tab: 'ngay' | 'thang', label: string) => (
    <Link to={dataImportLink(tab, status.month)}>
      <Button size="small">{label}</Button>
    </Link>
  );
  const legacy = (status.legacyRanges || [])
    .map((range) => formatPeriod(range.start, range.end))
    .join(', ');

  switch (status.status) {
    // Tháng đang chạy: dải lịch đã nêu ngày còn thiếu, không cần thêm khối.
    case 'DAILY_INCOMPLETE':
      return (
        <Alert
          type="error"
          showIcon
          message={`Tháng ${month} đang nhập theo ngày, còn thiếu ${missing.length} ngày`}
          description={
            view === 'ngay'
              ? `${list}. Nhập bù ở ô phía trên; đủ ngày thì không cần file tháng. File tháng của tháng này bị chặn cho tới khi đủ ngày.`
              : `${list}. Hãy nhập bù ở tab Data ngày; đủ ngày thì không cần file tháng. File tháng đang bị chặn${
                  isAdmin
                    ? '. Nếu TikTok không còn xuất được các ngày này, Admin có thể nhập file tháng kèm lý do'
                    : ''
                }.`
          }
          action={view === 'thang' ? goTo('ngay', 'Mở Data ngày') : undefined}
        />
      );
    case 'DAILY_COMPLETE':
      return (
        <Alert
          type="success"
          showIcon
          message={`Tháng ${month} đã đủ ${status.totalDays}/${status.totalDays} ngày theo file ngày: không cần file tháng`}
          description={
            view === 'thang'
              ? 'BXH, Thưởng Online, Phí Agency đang tính từ cộng các ngày. Muốn chốt theo số chính thức của TikTok thì vẫn nhập được file tháng; màn xem trước có bảng đối chiếu.'
              : 'BXH, Thưởng Online, Phí Agency tính từ cộng các ngày.'
          }
        />
      );
    case 'MONTH_FILE':
      if (view !== 'ngay') return null;
      return (
        <Alert
          type="info"
          showIcon
          message={`Tháng ${month} đã chốt bằng file tháng`}
          description="Ngày còn trống không bắt buộc bù. File ngày nhập thêm chỉ bổ sung số từng ngày cho Điểm Sao và phân tích, không đổi tổng tháng."
        />
      );
    case 'NO_DATA':
      return view === 'ngay' ? (
        <Alert
          type="warning"
          showIcon
          message={`Tháng ${month} đã qua và chưa có dữ liệu`}
          description={`Không có file từng ngày thì nhập 1 file cả tháng ở tab Data tháng. Đã bắt đầu nhập theo ngày thì phải nhập đủ ${status.totalDays} ngày.`}
          action={goTo('thang', 'Mở Data tháng')}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message={`Tháng ${month} chưa có số liệu`}
          description="Tháng đã qua mà không có file từng ngày: nhập 1 file cả tháng ở ô bên dưới."
        />
      );
    case 'LEGACY':
      return (
        <Alert
          type="warning"
          showIcon
          message={`Tháng ${month} chưa có file tháng`}
          description={
            view === 'thang'
              ? legacyDetail ||
                `Bảng xếp hạng đang tính từ số cộng dồn ${legacy} nhập trước đây. Nhập file cả tháng để chốt số.`
              : `Tháng có số cộng dồn ${legacy} nhập trước đây. Nhập file cả tháng ở tab Data tháng để chốt số${
                  missing.length ? `, hoặc nhập các ngày còn lại (${list})` : ''
                }.`
          }
          action={view === 'ngay' ? goTo('thang', 'Mở Data tháng') : undefined}
        />
      );
    default:
      return null;
  }
};

/**
 * Một khối nói tháng đang ở trạng thái nào và cần làm gì
 * (docs/nhap-du-lieu/DAC-TA-TRANG-THAI-THANG-2026-10-02.md): tháng đang chạy nhập từng ngày,
 * tháng đã nhập theo ngày phải bù đủ ngày, tháng đã qua không có dữ liệu ngày thì nhập 1 file tháng.
 */
const MonthStatusAlert = (props: Props) => {
  const alert = renderAlert(props);
  return alert ? <div className={styles.section}>{alert}</div> : null;
};

export default MonthStatusAlert;
